import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon, TransferOutIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, VFC } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import Accordion from 'dashboard/components/Accordion';
import Divider from 'dashboard/components/Divider';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import {
  ClearButton,
  DividerWithOrContainer,
  FooterDivider,
  ResultsContainer,
  ResultsRow,
  RightAlignedButtonGroup,
  StyledBanner,
  StyledDivider,
  StyledFileUpload,
  StyledList,
  StyledModalBody,
  StyledModalContainer,
  StyledModalHeader,
} from 'dashboard/components/WorkflowManualRunModal/styles';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import Form from 'dashboard/layouts/FormLayout/Form';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ManualRunResults } from 'dashboard/redux/reducers/workflow-runs';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { readFile } from 'lib/file';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';

// Each line: 28 chars (token) + 2 (buffer & line break) = 30 chars.
//  1000 * 30chars + 10% buffer = ~33000
const MAX_FILE_SIZE_WITH_BUFFER = 33000;
const MAX_OBJECT_ID_COUNT = 1000;
interface FormData {
  file?: FileWithPreview;
  text?: string;
}
interface WorkflowManualRunModalProps {
  clearResults: () => void;
  results?: ManualRunResults;
  onClose: () => void;
  onDone?: () => void;
  onSubmit: (objectIds: string[]) => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
  isLoading: boolean;
}

const REQUIREMENT_COPY = [
  'Object tokens must be valid and of the same type',
  'Objects must belong to this environment',
  'Objects must be compatible with the Workflow version trigger',
];

const WorkflowManualRunModal: VFC<WorkflowManualRunModalProps> = ({
  results,
  clearResults,
  onClose,
  onDone,
  onSubmit,
  showMessage,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
    watch,
  } = useForm<FormData>({
    mode: 'onChange',
  });
  const textValue = watch('text', '');
  const objectIdCountFromTextInput = textValue
    ? textValue.split(',').filter((str) => str.trim() !== '').length
    : 0;

  const resetFormAndResults = () => {
    reset();
    clearResults();
  };

  const handleClose = () => {
    resetFormAndResults();
    onClose();
  };

  const handleDoneClick = () => {
    if (onDone != null) onDone();
    handleClose();
  };

  const handleFormSubmit = async (formData: FormData) => {
    let objectIds: string[] = [];
    if (formData.file) {
      try {
        // we limit the number of rows to 3000 to prevent the handling of large files so no need to handle
        // streaming large files in the browser. Once the use cases has outgrown this limit, files should be handled
        // by the importer framework.
        const contents = (await readFile(formData.file, true)) as string;
        // Split the content string into rows based on newline characters.
        // This handles different newline formats from various platforms:
        const rows = contents.split(/\r\n|\r|\n/).filter((row) => row.trim() !== '');
        objectIds = rows.slice(1);
      } catch (error) {
        showMessage('Failed to parse csv', MessageType.Failure);
        return;
      }
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      objectIds = formData.text.split(',').map((id: string) => id.trim());
    }
    onSubmit(objectIds);
  };

  const renderFormBody = () => (
    <>
      <Paragraph>
        Trigger this Workflow directly by passing in Object IDs or Workflow Run IDs. This action
        generates new runs
      </Paragraph>
      <Spacer height={2} />
      <Accordion title='Requirements' defaultExpanded={false}>
        <Divider full />
        <StyledList>
          {REQUIREMENT_COPY.map((req) => (
            <li key={req}>{req}</li>
          ))}
        </StyledList>
      </Accordion>
      <Spacer height={2} />
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <FormControlLabel htmlFor='text'>Add IDs</FormControlLabel>
        <Controller
          name='text'
          control={control}
          rules={{
            validate: (value) => {
              if (value && value.trim() !== '') {
                if (watch('file')) {
                  return 'Please provide only one input: text or a csv';
                }

                const objectIds = value.split(',').filter((str) => str.trim() !== '');
                if (objectIds.some((id) => !id.match(/[a-z]+_[a-zA-Z0-9]{24,28}/))) {
                  return 'Please provide valid object IDs';
                }

                if (objectIds.length > MAX_OBJECT_ID_COUNT) {
                  return 'Please provide 1000 or fewer object IDs';
                }
              }
            },
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <>
                <TextArea
                  name='Add IDs'
                  placeholder='Add workflow run or object IDs separated by commas (i.e. inq_123, inq_124)'
                  onChange={onChange}
                  value={value}
                  disabled={isLoading}
                />
                {objectIdCountFromTextInput > 0 && (
                  <Paragraph size='sm'>
                    {objectIdCountFromTextInput} of {MAX_OBJECT_ID_COUNT} max objects found
                  </Paragraph>
                )}
                {errors['text'] && <FormError>{errors['text'].message}</FormError>}
              </>
            );
          }}
        />
        <DividerWithOrContainer>
          <Divider full />
          <span>OR</span>
          <Divider full />
        </DividerWithOrContainer>
        <Controller
          control={control}
          name='file'
          rules={{
            validate: (value) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              if (value != null && watch('text') && watch('text').trim() !== '') {
                return 'Please provide only one input: text or a csv';
              }
            },
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <>
                <StyledFileUpload
                  accept={['text/csv']}
                  value={value}
                  onChange={onChange}
                  onReject={(fileRejections: FileRejection[]) => {
                    const errorMessage = fileRejections[0]?.errors[0]?.message;

                    showMessage(
                      `Failed to upload. ${errorMessage ?? 'Unknown error'}`,
                      MessageType.Failure,
                    );
                  }}
                  disabled={isLoading}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  preview={null}
                  maxSize={MAX_FILE_SIZE_WITH_BUFFER}
                  clearButton={(
                    onClear: (e: MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void,
                  ) => (
                    <ClearButton
                      aria-label='Remove image'
                      onClick={onClear}
                      width='icon'
                      variant='inverse'
                      id='workflow-manual-run-modal__form-clear-button'
                    >
                      <TrashIcon />
                    </ClearButton>
                  )}
                  label={
                    <>
                      <div>
                        <Paragraph>Upload a CSV of object IDs.</Paragraph>
                        <Paragraph>The first row of the file must be a header.</Paragraph>
                        <Paragraph>File size must be less than 32kb.</Paragraph>
                      </div>
                      <Button
                        name='Upload CSV'
                        width='auto'
                        disabled={isLoading}
                        prefixIcon={<TransferOutIcon />}
                      >
                        Upload CSV
                      </Button>
                    </>
                  }
                />
                {errors['file'] && <FormError>{errors['file'].message}</FormError>}
              </>
            );
          }}
        />
        <FooterDivider full />
        <RightAlignedButtonGroup spacing={Spacing[2]}>
          <Button
            width='auto'
            variant='basic'
            id='workflow-manual-run-modal__form-cancel-button'
            name='Cancel'
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            width='auto'
            type='submit'
            variant='primary'
            name='Continue'
            isPending={isLoading}
            disabled={!isValid || !isDirty || isSubmitting || isLoading}
            id='workflow-manual-run__form-continue-button'
          >
            Create runs
          </Button>
        </RightAlignedButtonGroup>
      </Form>
    </>
  );

  const renderResultBody = () => (
    <>
      <ResultsContainer>
        <Label>Summary</Label>
        <Divider />
        <ResultsRow>
          <Paragraph>Valid Objects</Paragraph>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <Paragraph>{results && results.validObjectIds.length}</Paragraph>
        </ResultsRow>
        <ResultsRow>
          <Paragraph>Invalid Objects</Paragraph>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <Paragraph>{results && results.invalidObjectIds.length}</Paragraph>
        </ResultsRow>
        <Spacer height={1} />
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {results.validObjectIds.length > 0 && (
          <StyledBanner variant='success'>
            <CircleCheckIcon color={Color.CoreGreen600} />
            Runs for all valid objects will trigger soon.
          </StyledBanner>
        )}
        <Spacer height={1} />
      </ResultsContainer>
      <FooterDivider />
      <RightAlignedButtonGroup>
        <Button
          name='Back'
          width='auto'
          id='workflow-manual-run-modal__resutls-back-button'
          variant='basic'
          onClick={resetFormAndResults}
        >
          Back
        </Button>
        <Button
          name='Done'
          width='auto'
          variant='primary'
          onClick={handleDoneClick}
          id='workflow-manual-run-modal__resutls-done-button'
        >
          Done
        </Button>
      </RightAlignedButtonGroup>
    </>
  );

  return (
    <StyledModalContainer>
      <StyledModalHeader onClose={handleClose}>Create new runs</StyledModalHeader>
      <StyledDivider full />
      <StyledModalBody>{results != null ? renderResultBody() : renderFormBody()}</StyledModalBody>
    </StyledModalContainer>
  );
};

export default WorkflowManualRunModal;
